/**
 * Pending-mark overlay for the press station — a fix for a MONEY BUG that exists today, with no
 * realtime involved (`docs/realtime-updates-plan-v1.md` Phase 2).
 *
 * ## The bug
 *
 * `onMutate` calls `cancelQueries(['press-worklist'])`, which cancels refetches **in flight at that
 * moment**. It cannot cancel one that STARTS afterwards while the POST is still flying. And every read
 * behind `/press/worklist` is eventually consistent — `listBatches`, `listItems` (batch-store) and
 * `listLines` (order-store) carry no `consistentRead`, while `blank-inventory-store` sets it ten times
 * over. The absence is meaningful, not accidental.
 *
 * So this really happens: the operator ticks garment A, ticks garment B a second later, A's `onSettled`
 * fires an invalidate, that refetch resolves against a pre-write replica, and **B's box un-checks**. The
 * operator sees an unticked box for a garment they just did — and presses it twice. `inFlightBatches`
 * only disables rows in the SAME batch, so two runs on screen is all it takes.
 *
 * ## The fix
 *
 * Keep a map of marks that are pending confirmation and **re-apply them on top of every fetch result**,
 * so arrival order stops mattering. Re-application reuses `applyOptimisticMark` — the same reviewed,
 * surgical helper the optimistic path already uses — rather than reimplementing the flip.
 *
 * ## Why the clearing rules are what they are
 *
 * An overlay that never expires is the opposite bug: it would show a tick the server disagrees with, so
 * a garment that still needs pressing looks done and gets SKIPPED. So an entry is dropped when:
 *
 *  1. **The server agrees** — the fresh result already shows that state, making the entry redundant.
 *     Self-verifying, and needs no server timestamp.
 *  2. **`PENDING_TTL_MS` elapses** — the bounded safety valve. Worst case a wrong entry survives 5 s.
 *  3. **The mutation errors** — the caller drops it, and the existing surgical rollback owns the state.
 *
 * ⚠️ Deliberately NOT cleared in `onSettled`: `onSettled` *starts* the reconciling refetch, which lands
 * ~300 ms later against an eventually-consistent read. Clearing there would reopen the exact window this
 * closes.
 */
import type { PressWorklistResponse } from '@fsp/contracts';
import {
  applyOptimisticMark,
  type OptimisticMarkTarget,
  pressedCopiesOf,
} from './press-station-lib';

/** How long a mark may stay applied without server confirmation before it is abandoned. */
export const PENDING_TTL_MS = 5_000;

export type PendingMark = {
  target: OptimisticMarkTarget;
  /** ISO stamp handed to `applyOptimisticMark`; only its presence drives the checkbox. */
  at: string;
  addedAtMs: number;
};

export type PendingMarks = ReadonlyMap<string, PendingMark>;

/**
 * Identity of the PLACEMENT, deliberately excluding `pressed`.
 *
 * So a tick-then-untick of the same box replaces the entry instead of stacking two contradictory ones —
 * last intent wins, which is what the operator means.
 */
export function pendingKey(target: OptimisticMarkTarget): string {
  return [
    target.batchId,
    target.method,
    target.storeId,
    target.orderId,
    target.lineId,
    target.location,
    target.copyIndex ?? '-',
  ].join('|');
}

export function addPending(
  pending: PendingMarks,
  target: OptimisticMarkTarget,
  at: string,
  nowMs: number,
): PendingMarks {
  const next = new Map(pending);
  next.set(pendingKey(target), { target, at, addedAtMs: nowMs });
  return next;
}

export function dropPending(pending: PendingMarks, target: OptimisticMarkTarget): PendingMarks {
  const next = new Map(pending);
  next.delete(pendingKey(target));
  return next;
}

/**
 * Is this placement currently shown pressed by `data`?
 *
 * `undefined` = the placement is not in the worklist at all, which is normal: pressing the last
 * placement completes the garment and the batch drops off. We cannot confirm from absence, so the caller
 * keeps the entry until its TTL rather than guessing.
 */
export function isTargetPressedIn(
  data: PressWorklistResponse,
  target: OptimisticMarkTarget,
): boolean | undefined {
  const batch = data.batches.find((b) => b.batchId === target.batchId);
  const item = batch?.items.find(
    (it) =>
      it.method === target.method &&
      it.storeId === target.storeId &&
      it.orderId === target.orderId &&
      it.lineId === target.lineId &&
      it.location === target.location,
  );
  if (!item) return undefined;
  if (target.copyIndex === undefined) return item.pressedAt !== undefined;
  return pressedCopiesOf(item).includes(target.copyIndex);
}

/**
 * Drop entries the fresh result already agrees with, and any that have outlived `PENDING_TTL_MS`.
 *
 * Returns the SAME map reference when nothing was dropped. That matters: the caller feeds this into
 * `setPending`, and React bails out on an identical value — so an unchanged prune costs no extra render.
 */
export function prunePending(
  pending: PendingMarks,
  data: PressWorklistResponse,
  nowMs: number,
): PendingMarks {
  const doomed: string[] = [];
  for (const [key, mark] of pending) {
    if (nowMs - mark.addedAtMs >= PENDING_TTL_MS) doomed.push(key);
    else if (isTargetPressedIn(data, mark.target) === mark.target.pressed) doomed.push(key);
  }
  if (doomed.length === 0) return pending;
  const next = new Map(pending);
  for (const key of doomed) next.delete(key);
  return next;
}

/** Drop only the expired entries — the TTL sweep, for when no new server data has arrived. */
export function expirePending(pending: PendingMarks, nowMs: number): PendingMarks {
  const doomed = [...pending].filter(([, m]) => nowMs - m.addedAtMs >= PENDING_TTL_MS);
  if (doomed.length === 0) return pending;
  const next = new Map(pending);
  for (const [key] of doomed) next.delete(key);
  return next;
}

/**
 * Re-apply every pending mark on top of a server result.
 *
 * A placement that is no longer in the worklist is simply not matched — `applyOptimisticMark` returns
 * non-matching input unchanged — so a completed batch that has dropped off can never be resurrected by a
 * lingering entry.
 */
export function reapplyPending(
  data: PressWorklistResponse,
  pending: PendingMarks,
): PressWorklistResponse {
  // Same reference when there is nothing to apply — `applyOptimisticMark` rebuilds every array, so
  // without this the derived worklist would get a new identity on every render and defeat the
  // downstream memoisation of the press-run grouping.
  if (pending.size === 0) return data;
  let out = data;
  for (const mark of pending.values()) {
    out = applyOptimisticMark(out, mark.target, mark.at);
  }
  return out;
}
